import React, { useState, useEffect, useMemo } from 'react';
import { Alert, Form, Input, Select, DatePicker, Button, message, Row, Col, Spin, Table, Tag, Space, Drawer, Modal, Typography, Popconfirm } from 'antd';
import { ExclamationCircleOutlined, DeleteOutlined, SubnodeOutlined } from '@ant-design/icons';
import axiosConfig from '../../utils/axios-config';
import { useSelector } from 'react-redux';

import './asap-table.scss';

const { RangePicker } = DatePicker;
const { Text } = Typography;
const { Option } = Select;

const stripHtml = (value = '') => value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const AsapTable = () => {
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(true);
    const [tableData, setTableData] = useState([]);
    const [currentEntry, setCurrentEntry] = useState('');
    const currentUser = useSelector((state) => state.auth.currentUser);
    const [form] = Form.useForm();
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const [isMergeModalVisible, setIsMergeModalVisible] = useState(false);
    const [targetCaseId, setTargetCaseId] = useState(null);

    // --- TREE BUILDER ---
    const buildTree = (data) => {
        const map = {};
        const tree = [];
        data.forEach(item => {
            map[item.key] = { ...item, children: [] };
        });
        data.forEach(item => {
            if (item.parentCaseId && map[item.parentCaseId]) {
                map[item.parentCaseId].children.push(map[item.key]);
            } else {
                tree.push(map[item.key]);
            }
        });
        const finalTree = tree.map(node => {
            const cleanNode = (n) => {
                if (n.children && n.children.length === 0) {
                    delete n.children;
                } else if (n.children) {
                    n.children.forEach(cleanNode);
                }
            };
            cleanNode(node);
            return node;
        });
        return finalTree;
    };

    const getFlatSelectedRows = (data, keys) => {
        let selected = [];
        const recurse = (list) => {
            list.forEach(item => {
                if (keys.includes(item.key)) selected.push(item);
                if (item.children) recurse(item.children);
            });
        };
        recurse(data);
        return selected;
    };

    const selectedRowsData = useMemo(() =>
        getFlatSelectedRows(tableData, selectedRowKeys),
        [selectedRowKeys, tableData]);

    const hasHoowlaSelection = useMemo(() =>
        selectedRowsData.some(row => row.createdCaseId && row.createdCaseId !== ""),
        [selectedRowsData]);

    const mapResponseToData = (dataArray) => {
        const rawData = dataArray.map((res, index) => {
            const displayHoowlaId = (res.createdCaseId === 0 || res.createdCaseId === "0" || !res.createdCaseId) ? "" : res.createdCaseId;
            return {
                no: index + 1,
                key: res.id,
                caseId: res.id,
                parentCaseId: res.parentCaseId,
                caseStatus: res.caseStatus,
                createdCaseId: displayHoowlaId,
                addressLine1: res.addressLine1,
                addressPostcode: res.addressPostcode,
                createdTime: res.dateCreated ? res.dateCreated : '',
                statusComments: res.statusComments || "",
            };
        });
        return buildTree(rawData);
    };

    const handleSearch = async (values) => {
        setLoading(true);
        try {
            const response = await axiosConfig.post(`/api/casap-ocr-cases/filter`, values);
            setTableData(mapResponseToData(response.data.data));
            setSelectedRowKeys([]);
        } catch (error) {
            message.error("Failed to fetch data");
        } finally {
            setLoading(false);
        }
    };

    const handleGetAll = async () => {
        setLoading(true);
        try {
            const response = await axiosConfig.get(`/api/casap-ocr-cases/get-all`);
            setTableData(mapResponseToData(response.data.data));
            setSelectedRowKeys([]);
        } catch (error) {
            message.error("Failed to fetch data");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteSelected = async () => {
        setLoading(true);
        try {
            await axiosConfig.post(`/api/casap-ocr-cases/delete`, { selectedRowKeys });
            message.success("Deleted successfully");
            setSelectedRowKeys([]);
            handleSearch(form.getFieldsValue());
        } finally {
            setLoading(false);
        }
    };

    const openMergeModal = () => {
        if (selectedRowsData.length < 2) return;
        let initialTargetId = null;
        if (!hasHoowlaSelection) {
            const sorted = [...selectedRowsData].sort((a, b) => new Date(a.createdTime) - new Date(b.createdTime));
            initialTargetId = sorted[0]?.caseId;
        } else {
            initialTargetId = selectedRowsData.find(row => row.createdCaseId)?.caseId;
        }
        setTargetCaseId(initialTargetId);
        setIsMergeModalVisible(true);
    };

    const handleMergeSubmit = async () => {
        setLoading(true);
        try {
            const childrenCaseIds = selectedRowKeys.filter(id => id !== targetCaseId);
            await axiosConfig.post(`/api/casap-ocr-cases/merge`, {
                targetCaseId, childrenCaseIds, mergedBy: currentUser?.email
            });
            message.success("Merged successfully");
            setIsMergeModalVisible(false);
            setSelectedRowKeys([]);
            handleSearch(form.getFieldsValue());
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (currentUser?.id) handleGetAll();
    }, [currentUser]);

    const columns = [
        {
            title: 'No',
            dataIndex: 'no',
            key: 'no',
            width: 70,
            render: (value, record) => record.parentCaseId ? null : value
        },
        {
            title: 'Case Id',
            dataIndex: 'caseId',
            key: 'caseId',
            render: (id, row) => (
                <Space>
                    {row.parentCaseId && <SubnodeOutlined style={{ color: '#bfbfbf' }} />}
                    <span style={{ fontWeight: row.children ? 600 : 400 }}>
                        {row.caseStatus >= 2 ?
                            <a href={`${window.location.origin}/asap/cases/${id.toUpperCase()}`} target="_blank" rel="noopener noreferrer">{id.toUpperCase()}</a> :
                            id.toUpperCase()
                        }
                    </span>
                </Space>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'caseStatus',
            key: 'caseStatus',
            render: (status) => {
                const colors = { 1: "gold", 2: "volcano", 3: "purple", 4: "blue", 6: "cyan", 5: "green", 7: "magenta", 8: "gold", 666: "magenta" };
                const labels = { 1: "Received", 2: "Validate", 3: "Rejected", 4: "Manual", 6: "Automated", 5: "Completed", 7: "Check", 8: "Merged", 666: "Processing" };
                return <Tag color={colors[status] || "gold"}>{labels[status] || "Received"}</Tag>;
            },
        },
        { title: 'Hoowla ID', dataIndex: 'createdCaseId' },
        { title: 'Address', dataIndex: 'addressLine1' },
        { title: 'Post Code', dataIndex: 'addressPostcode' },
        {
            title: 'Created Time',
            dataIndex: 'createdTime',
            render: (text) => text ? new Date(text).toISOString().replace('T', ' ').substring(0, 19) : '',
        },
        {
            title: 'Comments',
            dataIndex: 'statusComments',
            render: (text) => {
                if (!text) return '';

                const previewText = stripHtml(text);

                if (previewText.length > 40) {
                    return (
                        <Space>
                            <span>{previewText.substring(0, 40)}...</span>
                            <Button type="link" size="small" onClick={() => { setCurrentEntry(text); setVisible(true); }}>
                                More
                            </Button>
                        </Space>
                    );
                }

                return <span dangerouslySetInnerHTML={{ __html: text }} />;
            }
        },
    ];

    return (
        <Spin spinning={loading}>
            <div className="sticky-filter-container">
                <Form form={form} layout="vertical" onFinish={handleSearch}>
                    <Row gutter={16} align="bottom">
                        <Col md={4}><Form.Item name="caseId" label="Case ID"><Input placeholder="ID" allowClear /></Form.Item></Col>
                        <Col md={4}>
                            <Form.Item name="statuses" label="Status">
                                <Select className="status-filter-select" mode="multiple" placeholder="Status" allowClear>
                                    <Option value={1}>Received</Option>
                                    <Option value={2}>Validate</Option>
                                    <Option value={3}>Rejected</Option>
                                    <Option value={4}>Manual</Option>
                                    <Option value={5}>Completed</Option>
                                    <Option value={6}>Automated</Option>
                                    <Option value={7}>Check</Option>
                                    <Option value={8}>Merged</Option>
                                    <Option value={666}>Processing</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col md={4}><Form.Item name="postCode" label="Post Code"><Input placeholder="Post Code" allowClear /></Form.Item></Col>
                        <Col md={5}><Form.Item name="createdRange" label="Created"><RangePicker style={{ width: '100%' }} /></Form.Item></Col>
                        <Col md={7} style={{ marginBottom: 24 }}>
                            <Space size="middle" style={{ width: '100%', justifyContent: 'flex-end' }}>
                                <Button type="primary" htmlType="submit">Search</Button>
                                <Button onClick={() => { form.resetFields(); handleSearch({}); }}>Reset</Button>
                                {selectedRowKeys.length > 0 && (
                                    <Popconfirm title={`Delete ${selectedRowKeys.length} items?`} onConfirm={handleDeleteSelected}>
                                        <Button type="primary" danger icon={<DeleteOutlined />}>Delete</Button>
                                    </Popconfirm>
                                )}
                                {selectedRowKeys.length > 1 && (
                                    <Button type="primary" onClick={openMergeModal} style={{ background: '#faad14', borderColor: '#faad14' }}>
                                        Merge ({selectedRowKeys.length})
                                    </Button>
                                )}
                            </Space>
                        </Col>
                    </Row>
                </Form>
            </div>

            <Table
                className="custom-tree-table"
                columns={columns}
                dataSource={tableData}
                rowKey="caseId"
                // Explicitly tell AntD where the children are
                childrenColumnName="children"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys,
                    checkStrictly: false,
                    getCheckboxProps: (record) => ({
                        disabled: !!record.parentCaseId,
                    }),
                }}
                expandable={{
                    expandIconColumnIndex: 0,
                    expandRowByClick: false,
                    // Ensure indent is enough for your custom icon
                    indentSize: 20,
                    expandIcon: ({ expanded, onExpand, record }) => {
                        // Check if record has children
                        const hasChildren = record.children && record.children.length > 0;

                        if (!hasChildren) {
                            return <span className="custom-expand-placeholder" />;
                        }

                        return (
                            <div
                                className={`custom-expand-btn ${expanded ? 'is-expanded' : ''}`}
                                onClick={e => {
                                    onExpand(record, e);
                                    e.stopPropagation();
                                }}
                            />
                        );
                    }
                }}
                pagination={{
                    defaultPageSize: 50,
                    pageSizeOptions: ['50', '100'],
                    showSizeChanger: true
                }}
            />

            <Modal
                title={<span><ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} /> Merge Cases</span>}
                visible={isMergeModalVisible}
                onOk={handleMergeSubmit}
                onCancel={() => setIsMergeModalVisible(false)}
                width={800}
                style={{ top: 40 }}
                zIndex={2000}
                okText="Confirm Merge"
                cancelText="Cancel"
            >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Alert
                        message="Merge Instructions"
                        description={
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                <li>Select the <b>Target Case</b> below. All other selected cases will become its children.</li>
                                <li>If a case already has a <b>Hoowla ID</b>, it is automatically preferred as the target.</li>
                            </ul>
                        }
                        type="info"
                        showIcon
                    />

                    <Typography.Text strong>Select Target Case:</Typography.Text>

                    <Table
                        size="small"
                        columns={columns.slice(1, 4)} // Shows Case ID, Status, and Hoowla ID
                        dataSource={selectedRowsData}
                        rowKey="caseId"
                        pagination={false}
                        rowSelection={{
                            type: 'radio',
                            selectedRowKeys: targetCaseId ? [targetCaseId] : [],
                            onChange: (keys) => setTargetCaseId(keys[0]),
                            getCheckboxProps: (record) => ({
                                // Guide: If someone has a Hoowla ID, you can't pick a different target 
                                // that DOESN'T have one (to maintain data integrity).
                                disabled: hasHoowlaSelection && !record.createdCaseId
                            }),
                        }}
                    />
                </Space>
            </Modal>

            <Drawer
                title='Status Comments'
                placement='bottom'
                closable={true}
                onClose={() => setVisible(false)}
                visible={visible}
                height="50vh"
                destroyOnClose={true}
            >
                <div style={{
                    background: '#f5f5f5',
                    padding: '15px',
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9'
                }}>
                    <div
                        style={{ wordBreak: 'break-word' }}
                        dangerouslySetInnerHTML={{ __html: currentEntry }}
                    />
                </div>
            </Drawer>
        </Spin>
    );
}

export default AsapTable;
